import { DescriptionsItemType } from 'antd/es/descriptions';
import { Descriptions } from 'antd/lib';
import { useTranslation } from 'translations/hooks';

import { SECONDARY_MAX_DECIMAL_DIGITS } from 'business/constants';
import { calculAverage } from 'business/shift-report/report/pages/ReportAggregated/services';
import { AggregatedReportType } from 'business/shift-report/report/types';
import { AggregatedShiftReportFragment } from 'generated/graphql';
import { createNumberFormatter } from 'technical/format/number';
import { notEmpty } from 'technical/utils/not-empty';

interface ChainingTableProps {
  type: AggregatedReportType;
  report: AggregatedShiftReportFragment;
  nbDays: number;
  nbWeeks: number;
}

function displayValueAndUnit(
  value: unknown,
  distanceUnit: string,
  formatter: Intl.NumberFormat,
) {
  return Number.isFinite(value)
    ? `${formatter.format(value as number)}\u00A0${distanceUnit}`
    : 'N/A';
}

export default function ChainingTable(props: ChainingTableProps) {
  const { type, report, nbDays, nbWeeks } = props;
  const { t, language } = useTranslation();

  const numberFormatter = createNumberFormatter(
    language,
    SECONDARY_MAX_DECIMAL_DIGITS,
  );

  const distanceItems = [
    {
      key: 'startMetricPoint',
      label: t('pages.print.startMetricPoint'),
      children: <span>{numberFormatter.format(report.startMetricPoint)}</span>,
      className: 'item',
    },
    {
      key: 'endMetricPoint',
      label: t('pages.print.endMetricPoint'),
      children: <span>{numberFormatter.format(report.endMetricPoint)}</span>,
      className: 'item',
    },
    {
      key: 'totalDistance',
      label: t('pages.print.totalDistance'),
      children: (
        <span>
          {displayValueAndUnit(
            report.distance,
            report.distanceUnit,
            numberFormatter,
          )}
        </span>
      ),
      className: 'item',
    },
  ] satisfies DescriptionsItemType[];

  const averageDistanceItems = [
    type === 'week' || type === 'month'
      ? {
          key: 'averageDistancePerDay',
          label: t('pages.print.averageDistancePerDay'),
          children: (
            <span>
              {displayValueAndUnit(
                calculAverage(report.distance, nbDays),
                report.distanceUnit,
                numberFormatter,
              )}
            </span>
          ),
          className: 'item',
        }
      : undefined,
    type === 'month'
      ? {
          key: 'averageDistancePerWeek',
          label: t('pages.print.averageDistancePerWeek'),
          children: (
            <span>
              {displayValueAndUnit(
                calculAverage(report.distance, nbWeeks),
                report.distanceUnit,
                numberFormatter,
              )}
            </span>
          ),
          className: 'item',
        }
      : undefined,
  ].filter(notEmpty) satisfies DescriptionsItemType[];

  return (
    <div>
      <Descriptions items={distanceItems} column={{ md: 1, lg: 3 }} />
      {type === 'week' || type === 'month' ? (
        <Descriptions
          items={averageDistanceItems}
          column={{ md: 1, lg: type === 'month' ? 2 : 1 }}
        />
      ) : null}
    </div>
  );
}
